/**
 * i18n bundle for the account-creation experience.
 *
 * Keyed by BCP-47 locale ("pt-BR", "en", "es-419", "fr-CA"). Every key is
 * present in every language so a missing translation is a compile-time
 * bug, not a runtime one.
 */

export const bundles = {
  "pt-BR": {
    languageName: "Português (Brasil)",
    langSelectorLabel: "Selecione o idioma da sua conta",
    langSelectorHint: "O idioma escolhido controla todo o conteúdo desta tela e a Política de Privacidade.",
    tabSignIn: "Entrar",
    welcomeToast: "Bem-vindo(a)!",
    tabSignUp: "Criar conta",
    tabForgot: "Recuperar senha",
    signInSubmit: "Entrar",
    signInPasswordPlaceholder: "Sua senha",
    forgotSubmit: "Gerar link",
    resetTokenLink: "Já tenho um token de recuperação",
    orDivider: "ou",
    googleCta: "Entrar com Google",
    googleHint: "Você será redirecionado(a) ao Google para escolher sua conta.",
    googleError: "Não foi possível concluir o login com Google. Tente novamente ou use seu e-mail e senha.",
    signUpTitle: "Crie sua conta",
    signUpSubtitle: "Precisamos apenas do seu nome, país de residência, e-mail, senha e sua aceitação da Política de Privacidade.",
    firstNameLabel: "Primeiro nome",
    firstNamePlaceholder: "Ex.: Ana",
    firstNameRequired: "Informe seu primeiro nome.",
    countryLabel: "País de residência",
    countryPlaceholder: "Selecione seu país",
    countryRequired: "Selecione seu país de residência.",
    emailLabel: "E-mail",
    emailPlaceholder: "voce@exemplo.com",
    passwordLabel: "Senha",
    passwordPlaceholder: "Mínimo de 8 caracteres",
    confirmPasswordLabel: "Confirmar senha",
    passwordTooShort: "A senha precisa ter ao menos 8 caracteres.",
    passwordMismatch: "As senhas não conferem.",
    invalidEmail: "Informe um e-mail válido.",
    privacySectionTitle: "Política de Privacidade",
    privacyReviewCta: "Revisar a Política de Privacidade",
    privacyClose: "Fechar",
    privacyMustReadFirst: "Você precisa abrir a Política de Privacidade antes de aceitar.",
    consentChoiceLabel: "Sua decisão sobre a Política de Privacidade",
    optInLabel:
      "Li, compreendi e concordo com a Política de Privacidade do My English Coach™ Interview Simulator. Autorizo o tratamento das minhas informações para as finalidades descritas nesta Política.",
    optOutLabel: "Não aceito a Política de Privacidade.",
    consentRequiredBadge: "Aceite obrigatório",
    consentAcceptedBadge: "Consentimento registrado",
    declineTitle: "Aceitação necessária",
    declineMessage:
      "É necessário aceitar a Política de Privacidade para criar uma conta e utilizar o My English Coach™ Interview Simulator.",
    declineMessage2:
      "Você pode revisar a Política de Privacidade e alterar sua escolha para aceitar, ou sair da criação da conta.",
    declineReviewCta: "Revisar e aceitar a Política de Privacidade",
    declineExitCta: "Sair da criação da conta",
    submitCta: "Criar conta",
    submitBusy: "Criando conta…",
    createFooter: "Ao criar sua conta você concorda com a Política de Privacidade selecionada acima.",
    policyVersionLabel: "Versão",
    // Post-signup Profile setup (Google fallback path)
    profileTitle: "Complete seu perfil",
    profileSubtitle: "Precisamos apenas do seu primeiro nome e do país onde você está.",
    profileSubmit: "Salvar e continuar",
    profileSubmitBusy: "Salvando…",
    profileLogout: "Sair",
    profileSaved: "Perfil salvo.",
  },

  en: {
    languageName: "English",
    langSelectorLabel: "Select the language of your account",
    langSelectorHint: "The chosen language controls all content on this screen and the Privacy Policy.",
    tabSignIn: "Sign in",
    welcomeToast: "Welcome!",
    tabSignUp: "Create account",
    tabForgot: "Recover password",
    signInSubmit: "Sign in",
    signInPasswordPlaceholder: "Your password",
    forgotSubmit: "Generate link",
    resetTokenLink: "I already have a recovery token",
    orDivider: "or",
    googleCta: "Sign in with Google",
    googleHint: "You will be redirected to Google to choose your account.",
    googleError: "Google sign-in could not be completed. Please try again or use your email and password.",
    signUpTitle: "Create your account",
    signUpSubtitle: "All we need is your name, country of residence, email, password, and your Privacy Policy consent.",
    firstNameLabel: "First name",
    firstNamePlaceholder: "e.g. Ana",
    firstNameRequired: "Enter your first name.",
    countryLabel: "Country of residence",
    countryPlaceholder: "Select your country",
    countryRequired: "Select your country of residence.",
    emailLabel: "Email",
    emailPlaceholder: "you@example.com",
    passwordLabel: "Password",
    passwordPlaceholder: "At least 8 characters",
    confirmPasswordLabel: "Confirm password",
    passwordTooShort: "The password must be at least 8 characters long.",
    passwordMismatch: "The passwords do not match.",
    invalidEmail: "Enter a valid email address.",
    privacySectionTitle: "Privacy Policy",
    privacyReviewCta: "Review the Privacy Policy",
    privacyClose: "Close",
    privacyMustReadFirst: "You must open the Privacy Policy before accepting.",
    consentChoiceLabel: "Your decision on the Privacy Policy",
    optInLabel:
      "I have read, understood, and agree to the Privacy Policy of the My English Coach™ Interview Simulator. I authorize the processing of my information for the purposes described in the Policy.",
    optOutLabel: "I do not accept the Privacy Policy.",
    consentRequiredBadge: "Consent required",
    consentAcceptedBadge: "Consent recorded",
    declineTitle: "Consent required",
    declineMessage:
      "You must accept the Privacy Policy to create an account and use the My English Coach™ Interview Simulator.",
    declineMessage2:
      "You may review the Privacy Policy and change your selection to accept it, or exit the account-creation process.",
    declineReviewCta: "Review and accept the Privacy Policy",
    declineExitCta: "Exit account creation",
    submitCta: "Create account",
    submitBusy: "Creating account…",
    createFooter: "By creating your account you agree to the Privacy Policy in the selected language.",
    policyVersionLabel: "Version",
    profileTitle: "Complete your profile",
    profileSubtitle: "We just need your first name and the country where you are.",
    profileSubmit: "Save and continue",
    profileSubmitBusy: "Saving…",
    profileLogout: "Sign out",
    profileSaved: "Profile saved.",
  },

  "es-419": {
    languageName: "Español (Latinoamérica)",
    langSelectorLabel: "Seleccione el idioma de su cuenta",
    langSelectorHint: "El idioma elegido controla todo el contenido de esta pantalla y la Política de Privacidad.",
    tabSignIn: "Iniciar sesión",
    welcomeToast: "¡Bienvenido/a!",
    tabSignUp: "Crear cuenta",
    tabForgot: "Recuperar contraseña",
    signInSubmit: "Iniciar sesión",
    signInPasswordPlaceholder: "Su contraseña",
    forgotSubmit: "Generar enlace",
    resetTokenLink: "Ya tengo un token de recuperación",
    orDivider: "o",
    googleCta: "Ingresar con Google",
    googleHint: "Será redirigido a Google para elegir su cuenta.",
    googleError: "No se pudo completar el inicio de sesión con Google. Inténtelo de nuevo o use su correo electrónico y contraseña.",
    signUpTitle: "Crea su cuenta",
    signUpSubtitle: "Solo necesitamos su nombre, país de residencia, correo, contraseña y su aceptación de la Política de Privacidad.",
    firstNameLabel: "Nombre",
    firstNamePlaceholder: "Ej.: Ana",
    firstNameRequired: "Ingrese su nombre.",
    countryLabel: "País de residencia",
    countryPlaceholder: "Seleccione su país",
    countryRequired: "Seleccione su país de residencia.",
    emailLabel: "Correo electrónico",
    emailPlaceholder: "su@ejemplo.com",
    passwordLabel: "Contraseña",
    passwordPlaceholder: "Mínimo 8 caracteres",
    confirmPasswordLabel: "Confirmar contraseña",
    passwordTooShort: "La contraseña debe tener al menos 8 caracteres.",
    passwordMismatch: "Las contraseñas no coinciden.",
    invalidEmail: "Ingrese un correo electrónico válido.",
    privacySectionTitle: "Política de Privacidad",
    privacyReviewCta: "Revisar la Política de Privacidad",
    privacyClose: "Cerrar",
    privacyMustReadFirst: "Debe abrir la Política de Privacidad antes de aceptar.",
    consentChoiceLabel: "Su decisión sobre la Política de Privacidad",
    optInLabel:
      "He leído, comprendido y acepto la Política de Privacidad del My English Coach™ Interview Simulator. Autorizo el tratamiento de mi información para los fines descritos en la Política.",
    optOutLabel: "No acepto la Política de Privacidad.",
    consentRequiredBadge: "Consentimiento obligatorio",
    consentAcceptedBadge: "Consentimiento registrado",
    declineTitle: "Se requiere consentimiento",
    declineMessage:
      "Es necesario aceptar la Política de Privacidad para crear una cuenta y utilizar My English Coach™ Interview Simulator.",
    declineMessage2:
      "Puede revisar la Política de Privacidad y cambiar su elección para aceptarla, o salir del proceso de creación de la cuenta.",
    declineReviewCta: "Revisar y aceptar la Política de Privacidad",
    declineExitCta: "Salir de la creación de la cuenta",
    submitCta: "Crear cuenta",
    submitBusy: "Creando cuenta…",
    createFooter: "Al crear su cuenta aceptas la Política de Privacidad en el idioma seleccionado.",
    policyVersionLabel: "Versión",
    profileTitle: "Completa su perfil",
    profileSubtitle: "Solo necesitamos su primer nombre y el país donde se encuentra.",
    profileSubmit: "Guardar y continuar",
    profileSubmitBusy: "Guardando…",
    profileLogout: "Salir",
    profileSaved: "Perfil guardado.",
  },

  "fr-CA": {
    languageName: "Français (Canada)",
    langSelectorLabel: "Sélectionnez la langue de votre compte",
    langSelectorHint: "La langue choisie contrôle tout le contenu de cet écran et la Politique de confidentialité.",
    tabSignIn: "Se connecter",
    welcomeToast: "Bienvenue !",
    tabSignUp: "Créer un compte",
    tabForgot: "Récupérer le mot de passe",
    signInSubmit: "Se connecter",
    signInPasswordPlaceholder: "Votre mot de passe",
    forgotSubmit: "Générer le lien",
    resetTokenLink: "J’ai déjà un jeton de récupération",
    orDivider: "ou",
    googleCta: "Se connecter avec Google",
    googleHint: "Vous serez redirig\u00E9 vers Google pour choisir votre compte.",
    googleError: "La connexion Google n\u2019a pas pu \u00EAtre finalis\u00E9e. Veuillez r\u00E9essayer ou utiliser votre courriel et votre mot de passe.",
    signUpTitle: "Créez votre compte",
    signUpSubtitle: "Il nous faut seulement votre prénom, votre pays de résidence, votre courriel, un mot de passe et votre consentement à la Politique de confidentialité.",
    firstNameLabel: "Prénom",
    firstNamePlaceholder: "Ex. : Ana",
    firstNameRequired: "Entrez votre prénom.",
    countryLabel: "Pays de résidence",
    countryPlaceholder: "Sélectionnez votre pays",
    countryRequired: "Sélectionnez votre pays de résidence.",
    emailLabel: "Courriel",
    emailPlaceholder: "vous@exemple.com",
    passwordLabel: "Mot de passe",
    passwordPlaceholder: "Au moins 8 caractères",
    confirmPasswordLabel: "Confirmer le mot de passe",
    passwordTooShort: "Le mot de passe doit contenir au moins 8 caractères.",
    passwordMismatch: "Les mots de passe ne correspondent pas.",
    invalidEmail: "Entrez une adresse courriel valide.",
    privacySectionTitle: "Politique de confidentialité",
    privacyReviewCta: "Consulter la Politique de confidentialité",
    privacyClose: "Fermer",
    privacyMustReadFirst: "Vous devez ouvrir la Politique de confidentialité avant d’accepter.",
    consentChoiceLabel: "Votre décision sur la Politique de confidentialité",
    optInLabel:
      "J’ai lu, compris et j’accepte la Politique de confidentialité du My English Coach™ Interview Simulator. J’autorise le traitement de mes renseignements aux fins décrites dans la Politique.",
    optOutLabel: "Je n’accepte pas la Politique de confidentialité.",
    consentRequiredBadge: "Consentement requis",
    consentAcceptedBadge: "Consentement enregistré",
    declineTitle: "Consentement requis",
    declineMessage:
      "Vous devez accepter la Politique de confidentialité pour créer un compte et utiliser My English Coach™ Interview Simulator.",
    declineMessage2:
      "Vous pouvez relire la Politique de confidentialité et modifier votre choix pour l’accepter, ou quitter le processus de création du compte.",
    declineReviewCta: "Relire et accepter la Politique de confidentialité",
    declineExitCta: "Quitter la création du compte",
    submitCta: "Créer le compte",
    submitBusy: "Création du compte…",
    createFooter: "En créant votre compte, vous acceptez la Politique de confidentialité dans la langue sélectionnée.",
    policyVersionLabel: "Version",
    profileTitle: "Complétez votre profil",
    profileSubtitle: "Nous avons seulement besoin de votre prénom et du pays où vous vous trouves.",
    profileSubmit: "Enregistrer et continuer",
    profileSubmitBusy: "Enregistrement…",
    profileLogout: "Se déconnecter",
    profileSaved: "Profil enregistré.",
  },
};

export const SUPPORTED_LANGUAGES = [
  { code: "pt-BR", label: bundles["pt-BR"].languageName, flag: "🇧🇷" },
  { code: "es-419", label: bundles["es-419"].languageName, flag: "🇪🇸" },
  { code: "fr-CA", label: bundles["fr-CA"].languageName, flag: "🇨🇦" },
  { code: "en", label: bundles.en.languageName, flag: "🇬🇧" },
];

/**
 * ISO 3166-1 alpha-2 country codes accepted during Beta signup.
 * The BACKEND stores/receives these canonical codes; the FRONTEND displays
 * a localised label. Order in the UI is alphabetical per language.
 */
export const SUPPORTED_COUNTRY_CODES = ["BR", "CA", "MX", "US"];

export const COUNTRY_LABELS = {
  "pt-BR": { BR: "Brasil", CA: "Canadá", MX: "México", US: "Estados Unidos" },
  en:      { BR: "Brazil", CA: "Canada", MX: "Mexico", US: "United States" },
  "es-419":{ BR: "Brasil", CA: "Canadá", MX: "México", US: "Estados Unidos" },
  "fr-CA": { BR: "Brésil", CA: "Canada", MX: "Mexique", US: "États-Unis" },
};

/**
 * Returns the supported countries as `{ code, label }` sorted alphabetically
 * in the requested locale (uses Intl.Collator for locale-correct ordering).
 */
export function getCountriesForLocale(locale) {
  const labels = COUNTRY_LABELS[locale] || COUNTRY_LABELS.en;
  const list = SUPPORTED_COUNTRY_CODES.map((code) => ({ code, label: labels[code] }));
  const collator = new Intl.Collator(locale, { sensitivity: "base" });
  list.sort((a, b) => collator.compare(a.label, b.label));
  return list;
}

export const SUPPORTED_LOCALE_CODES = SUPPORTED_LANGUAGES.map((l) => l.code);
export const FALLBACK_LOCALE = "en";

/**
 * Browser-language → supported-locale mapping.
 *
 * Any variant within a supported language family is accepted:
 *   pt / pt-*  → pt-BR    (Brazilian Portuguese)
 *   es / es-*  → es-419   (Latin American Spanish)
 *   fr / fr-*  → fr-CA    (Canadian French)
 *   en / en-*  → en       (English)
 *
 * A user in Quebec whose browser is set to `fr-FR` still lands on
 * Canadian French; a Colombian visitor whose browser is `es-CO` lands on
 * Latin American Spanish; and so on. Any language outside these four
 * families (de, ja, zh, hi, …) falls through to English.
 */
export function detectDefaultLocale(nav) {
  const navigatorRef = nav || (typeof navigator !== "undefined" ? navigator : null);
  if (!navigatorRef) return FALLBACK_LOCALE;

  const tags = [];
  if (Array.isArray(navigatorRef.languages)) {
    for (const t of navigatorRef.languages) if (typeof t === "string" && t) tags.push(t);
  }
  if (typeof navigatorRef.language === "string" && navigatorRef.language) {
    tags.push(navigatorRef.language);
  }

  const familyMap = { pt: "pt-BR", es: "es-419", fr: "fr-CA", en: "en" };

  for (const raw of tags) {
    const primary = raw.toLowerCase().split("-")[0];
    if (familyMap[primary]) return familyMap[primary];
  }

  return FALLBACK_LOCALE;
}

export const PRIVACY_POLICY_VERSION = "beta-1.1";

export function t(locale, key) {
  const bundle = bundles[locale] || bundles["pt-BR"];
  return bundle[key] ?? bundles["pt-BR"][key] ?? key;
}

export function getBundle(locale) {
  return bundles[locale] || bundles["pt-BR"];
}
